<script setup lang="ts">
/**
 * An older picture / video / file of an imported Facebook chat (api/app/fb_history.py): only the metadata was kept
 * (Meta's links expire), so it loads when someone clicks — copied into our storage once, then it stays. When
 * Facebook no longer has it, say so politely instead of a broken picture.
 */
import { computed, ref } from 'vue'
import { fbHistoryApi } from '../../api/fbhistory'
import type { Attachment } from '../../api/types'
import { lazyLabel } from '../../lib/fbHistory'
import Icon from '../ui/Icon.vue'

const props = defineProps<{ messageId: string; idx: number; att: Attachment; mine: boolean }>()

const state = ref<'idle' | 'loading' | 'ok' | 'error'>('idle')
const url = ref<string | null>(null)
const error = ref<string | null>(null)
const label = computed(() => lazyLabel(props.att.type))

async function load(): Promise<void> {
  if (state.value === 'loading') return
  state.value = 'loading'
  error.value = null
  try {
    const r = await fbHistoryApi().media(props.messageId, props.idx)
    url.value = r.url
    state.value = 'ok'
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e)
    state.value = 'error'
  }
}
</script>

<template>
  <a v-if="state === 'ok' && url && att.type === 'image'" class="fl-img" :href="url" target="_blank" rel="noopener noreferrer"
    :aria-label="`เปิด${att.name ? ' ' + att.name : 'รูปภาพ'}ในแท็บใหม่`"
    ><img :src="url" :alt="att.name ?? 'รูปภาพ'"
  /></a>
  <video v-else-if="state === 'ok' && url && att.type === 'video'" class="fl-vid" :src="url" controls playsinline preload="metadata"></video>
  <a v-else-if="state === 'ok' && url" class="fl att-like" :class="{ mine }" :href="url" target="_blank" rel="noopener noreferrer"
    ><Icon name="external" /><span class="fl-b"><b>{{ att.name ?? 'ไฟล์' }}</b><span>เปิดไฟล์</span></span></a
  >
  <div v-else class="fl" :class="{ mine }">
    <Icon :name="state === 'error' ? 'alertCircle' : 'image'" />
    <span class="fl-b">
      <b>{{ label }}<template v-if="att.name"> · {{ att.name }}</template></b>
      <span v-if="state === 'error'" class="fl-err" role="status">{{ error }}</span>
      <span v-else-if="state === 'loading'">กำลังโหลดจาก Facebook…</span>
      <button v-else type="button" class="fl-go" @click="load">กดเพื่อโหลดจาก Facebook</button>
    </span>
  </div>
</template>

<style scoped>
.fl {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 220px;
  max-width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f4f6f5;
  color: var(--mut);
  font-size: 12.5px;
  line-height: 19px;
  white-space: normal;
  text-decoration: none;
}
.fl.mine {
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
}
.fl > :deep(.ic) {
  flex: none;
  width: 18px;
  height: 18px;
}
.fl-b {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.fl-b b {
  font-weight: 600;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.fl.mine .fl-b b {
  color: #fff;
}
.fl-go {
  align-self: flex-start;
  padding: 0;
  background: none;
  font: inherit;
  font-weight: 600;
  color: var(--ac2);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.fl.mine .fl-go {
  color: #fff;
}
.fl-err {
  color: #7a4700;
}
.fl.mine .fl-err {
  color: #fff;
}
.fl-img {
  display: block;
  line-height: 0;
  border-radius: 12px;
  overflow: hidden;
  background: #eef2f0;
}
.fl-img img,
.fl-vid {
  display: block;
  max-width: min(280px, 100%);
  max-height: 320px;
  min-width: 80px;
  min-height: 60px;
  object-fit: cover;
  border-radius: 12px;
}
</style>
